import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import {
  EmailAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { useAuthStore } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';
import { Card, CardContent, Button, Checkbox, Input } from '@/components/ui';

const SETTINGS_DOC_ID = 'preferences';
const WITHDRAW_CONFIRM_TEXT = '退会します';

function getWithdrawErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'パスワードが正しくありません';
      case 'auth/requires-recent-login':
        return '退会には再ログインが必要です。再度ログインしてください';
      default:
        return '退会処理に失敗しました。しばらくしてから再度お試しください';
    }
  }

  return '退会処理に失敗しました。しばらくしてから再度お試しください';
}

export function WithdrawPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const addToast = useToastStore((state) => state.addToast);

  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canWithdraw =
    password.length > 0 &&
    confirmationText === WITHDRAW_CONFIRM_TEXT &&
    isAgreed;

  const handleWithdraw = async () => {
    if (!user?.uid) return;
    if (!canWithdraw) {
      addToast('warning', '確認項目をすべて入力してください');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      addToast('error', 'ユーザー情報の取得に失敗しました');
      return;
    }

    try {
      setIsSubmitting(true);

      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        status: 'withdrawn',
        withdrawnAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const settingsRef = doc(db, 'users', user.uid, 'settings', SETTINGS_DOC_ID);
      await setDoc(
        settingsRef,
        {
          notifications: {
            email: {
              messages: false,
              profileRequests: false,
              reviews: false,
              marketing: false,
            },
            push: {
              messages: false,
              profileRequests: false,
              reviews: false,
            },
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await deleteUser(currentUser);
      addToast('success', '退会処理が完了しました');
      navigate('/login', { replace: true });
    } catch (error) {
      addToast('error', getWithdrawErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <Link
        to="/mypage"
        className="inline-flex items-center gap-2 text-neutral-600 hover:text-copper mb-6 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">マイページに戻る</span>
      </Link>

      <h1 className="text-2xl font-heading-luxury text-luxury-dark mb-6">
        退会
      </h1>

      <Card className="border-red-200">
        <CardContent className="py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h2 className="font-bold text-red-600">本当に退会しますか？</h2>
              <p className="text-sm text-neutral-600">この操作は取り消せません</p>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700 leading-relaxed">
              退会処理を行うと、ログインできなくなります。
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
              <li>認証アカウントが削除されます</li>
              <li>ユーザーステータスは「退会済み」に更新されます</li>
              <li>通知設定はすべてオフになります</li>
            </ul>
          </div>

          <div className="space-y-4 mb-6">
            <Input
              label="現在のパスワード"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              label={`確認のため「${WITHDRAW_CONFIRM_TEXT}」と入力してください`}
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
            />
            <Checkbox
              label="退会後はログインできないことを理解しました"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Link to="/mypage">
              <Button variant="secondary">キャンセル</Button>
            </Link>
            <Button
              variant="danger"
              onClick={handleWithdraw}
              isLoading={isSubmitting}
              disabled={!canWithdraw}
            >
              退会する
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
